import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, LogOut, Pencil, CheckCircle2, Briefcase, AlertTriangle, MapPin, Calendar, Clock, Camera, KeyRound, DollarSign, Eye, XCircle, Landmark } from "lucide-react";
import { toast } from "sonner";
import confetti from "canvas-confetti";

import SEO from "@/components/shared/SEO";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import LiveSelfieCapture from "@/components/shared/LiveSelfieCapture";
import PayoutSetupDialog from "@/components/cleaner/PayoutSetupDialog";
import CheckInModal from "@/components/shared/CheckInModal";
import { cleanerApi, getCleanerToken, setCleanerToken, unwrap, api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

const LANGUAGES = [
  { code: "en", label: "English" }, { code: "es", label: "Español" },
  { code: "pt", label: "Português" }, { code: "fr", label: "Français" }, { code: "it", label: "Italiano" },
];

function Avatar({ src, name, size = 72 }) {
  const initials = (name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  if (src) return <img src={src} alt={name} width={size} height={size} style={{ width: size, height: size }} className="rounded-full object-cover shrink-0" />;
  return <div style={{ width: size, height: size, fontSize: size / 2.8 }} className="rounded-full bg-[#0EA5A4]/15 text-[#0F766E] grid place-items-center font-bold shrink-0">{initials}</div>;
}

function daysLeft(deadlineIso) {
  if (!deadlineIso) return null;
  const diff = new Date(deadlineIso).getTime() - Date.now();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

export default function CleanerDashboardPage() {
  const { locale } = useI18n();
  const navigate = useNavigate();
  const L = (en, es) => (locale === "es" ? es : en);
  const [me, setMe] = useState(null);          // { user, cleaner, bookings }
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [recheckOpen, setRecheckOpen] = useState(false);
  const [recheckSubmitting, setRecheckSubmitting] = useState(false);
  const [checkinFor, setCheckinFor] = useState(null);   // booking id currently checking in
  const [checkinSubmitting, setCheckinSubmitting] = useState(false);
  const [acceptingId, setAcceptingId] = useState(null);
  const [decliningId, setDecliningId] = useState(null);
  const [declineTarget, setDeclineTarget] = useState(null);
  const [payoutLoading, setPayoutLoading] = useState(false);
  const [payoutExplainerOpen, setPayoutExplainerOpen] = useState(false);

  const startPayoutSetup = async () => {
    setPayoutLoading(true);
    try {
      const r = await cleanerApi.post("/cleaner/stripe/onboarding-link");
      const url = unwrap(r)?.onboarding_url;
      if (!url) throw new Error("no url");
      window.location.href = url;
    } catch (err) {
      setPayoutLoading(false);
      toast.error(err?.response?.data?.detail?.message ||
        L("Could not open the payout setup. Please try again.", "No pudimos abrir la configuración de pagos. Intenta de nuevo."));
    }
  };

  const load = useCallback(async () => {
    if (!getCleanerToken()) { navigate("/cleaner/login"); return; }
    try {
      const r = await cleanerApi.get("/cleaner/me");
      const data = unwrap(r);
      if (data?.user?.must_change_password) {
        navigate("/cleaner/change-password", { replace: true });
        return;
      }
      setMe(data);
    } catch {
      setCleanerToken(null);
      navigate("/cleaner/login");
    } finally { setLoading(false); }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const logout = () => { setCleanerToken(null); navigate("/cleaner/login"); };

  const submitRecheckSelfie = async (blob /* , dataUrl */) => {
    setRecheckSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, "recheck.jpg");
      await cleanerApi.post("/cleaner/recheck-selfie", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(L("Selfie submitted. Our team will review it shortly.",
                      "Selfie enviada. Nuestro equipo la va a revisar en breve."));
      setRecheckOpen(false);
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail?.message || e?.message || "Upload failed";
      toast.error(msg);
    } finally { setRecheckSubmitting(false); }
  };

  const submitCheckin = async ({ blob, lat, lng, accuracy }) => {
    if (!checkinFor) return;
    setCheckinSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, "checkin.jpg");
      fd.append("lat", String(lat));
      fd.append("lng", String(lng));
      fd.append("accuracy_meters", String(accuracy || ""));
      const r = await cleanerApi.post(`/cleaner/bookings/${checkinFor}/checkin`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const data = unwrap(r);
      toast.success(L("Check-in saved. The customer was notified.",
                      "Check-in guardado. El cliente fue notificado."));
      if (data?.far_from_address) {
        toast.warning(L(`We logged your location ${Math.round(data.distance_meters)}m away from the address. The admin team has been alerted.`,
                        `Registramos tu ubicación a ${Math.round(data.distance_meters)}m de la dirección. El equipo fue notificado.`));
      }
      setCheckinFor(null);
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail?.message || e?.message || "Check-in failed";
      toast.error(msg);
    } finally { setCheckinSubmitting(false); }
  };

  const acceptJob = async (bookingId) => {
    setAcceptingId(bookingId);
    try {
      await cleanerApi.post(`/cleaner/bookings/${bookingId}/accept`);
      const b = (me?.bookings || []).find((x) => x.id === bookingId);
      confetti({ particleCount: 120, spread: 80, origin: { y: 0.6 } });
      toast.success(L(`Job accepted — see you on ${b?.preferred_date || "the day"}`,
                      `Trabajo aceptado — te vemos el ${b?.preferred_date || "día acordado"}`));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Could not accept the job");
    } finally { setAcceptingId(null); }
  };

  const declineJob = async () => {
    if (!declineTarget) return;
    setDecliningId(declineTarget);
    try {
      await cleanerApi.post(`/cleaner/bookings/${declineTarget}/decline`);
      toast.success(L("Job declined", "Trabajo rechazado"));
      setDeclineTarget(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Could not decline the job");
    } finally { setDecliningId(null); }
  };

  if (loading) return <div className="min-h-screen grid place-items-center bg-[#FAFAF9]"><Loader2 className="w-6 h-6 animate-spin text-[#0EA5A4]" /></div>;

  const profile = me?.cleaner;
  const bookings = me?.bookings || [];
  const payoutsReady = profile?.payout_status === "ready";
  const earnings = me?.earnings_summary || {};
  const firstName = (me?.user?.full_name || profile?.full_name || "").split(" ")[0] || L("there", "");
  const showRecheckBanner = !!profile?.needs_recheck && profile?.recheck_status !== "pending_review";
  const recheckPending = profile?.recheck_status === "pending_review";
  const dLeft = daysLeft(profile?.recheck_deadline);

  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="cleaner-dashboard-page">
      <SEO title={L("Cleaner dashboard — Ever Clean Home", "Panel de limpiador — Ever Clean Home")} noindex />
      <header className="bg-white border-b border-[#E5E7EB]">
        <div className="max-w-4xl mx-auto px-4 md:px-8 h-16 md:h-20 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/brand/logo-everclean-wordmark.png" alt="Ever Clean Home" className="h-11 md:h-14 w-auto object-contain" />
          </div>
          <button onClick={logout} className="flex items-center gap-2 text-sm font-medium text-[#4B5563] hover:text-[#0EA5A4]" data-testid="cleaner-logout">
            <LogOut className="w-4 h-4" /> {L("Log out", "Salir")}
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 md:px-8 py-10 space-y-6">
        {/* Payout onboarding banner (iter33 — Fase 3, Bloque A) */}
        {profile && !payoutsReady && (
          <div className="rounded-2xl border border-amber-300 bg-amber-50 p-5 flex flex-col md:flex-row gap-4 md:items-center" data-testid="payout-setup-banner">
            <div className="flex items-start gap-3 flex-1">
              <span className="w-10 h-10 rounded-xl bg-amber-200 grid place-items-center shrink-0">
                <Landmark className="w-5 h-5 text-amber-700" />
              </span>
              <div>
                <div className="font-semibold text-amber-900">
                  {profile.payout_status === "in_progress"
                    ? L("Your payout setup isn't finished", "Tu configuración de pagos no está terminada")
                    : L("You haven't connected your bank account yet", "Todavía no conectaste tu cuenta bancaria")}
                </div>
                <div className="text-sm text-amber-800 mt-0.5">
                  {L("Without this step you can accept jobs but you won't receive payments. It takes about 5 minutes — Stripe handles all the sensitive data.",
                     "Sin este paso puedes aceptar trabajos pero no vas a recibir pagos. Toma unos 5 minutos — Stripe maneja todos los datos sensibles.")}
                </div>
              </div>
            </div>
            <Button onClick={() => setPayoutExplainerOpen(true)} disabled={payoutLoading}
              className="rounded-xl bg-amber-600 hover:bg-amber-700 text-white shrink-0" data-testid="payout-connect-btn">
              {payoutLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <>{profile.payout_status === "in_progress"
                  ? L("Finish setup", "Terminar configuración")
                  : L("Connect now", "Conectar ahora")}</>
              )}
            </Button>
          </div>
        )}

        {/* Re-verification banner (iter18 — Bloque D, Capa 2) */}
        {showRecheckBanner && (
          <div
            className="rounded-2xl border border-amber-300 bg-amber-50 p-5 flex flex-col md:flex-row gap-4 md:items-center"
            data-testid="recheck-banner"
          >
            <div className="flex items-start gap-3 flex-1">
              <span className="w-10 h-10 rounded-xl bg-amber-200 grid place-items-center shrink-0">
                <AlertTriangle className="w-5 h-5 text-amber-700" />
              </span>
              <div>
                <div className="font-semibold text-amber-900">
                  {L("Your identity needs to be re-verified",
                     "Tu identidad necesita re-verificación")}
                </div>
                <div className="text-sm text-amber-800 mt-0.5">
                  {L(`Please take a new selfie to keep your account active.${dLeft != null ? ` (${dLeft} days left)` : ""}`,
                     `Por favor tómate una nueva selfie para mantener tu cuenta activa.${dLeft != null ? ` (${dLeft} días restantes)` : ""}`)}
                </div>
              </div>
            </div>
            <Button
              onClick={() => setRecheckOpen(true)}
              className="rounded-xl bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="recheck-take-selfie"
            >
              <Camera className="w-4 h-4 mr-2" /> {L("Take selfie now", "Tomar selfie ahora")}
            </Button>
          </div>
        )}
        {recheckPending && (
          <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800" data-testid="recheck-pending">
            {L("Your new selfie is being reviewed by our team. We'll email you as soon as it's approved.",
               "Nuestro equipo está revisando tu nueva selfie. Te avisaremos por mail apenas la aprueben.")}
          </div>
        )}

        {/* Welcome */}
        <Card className="rounded-2xl p-8 border border-[#E5E7EB] bg-gradient-to-br from-[#0EA5A4]/[0.06] to-transparent">
          <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tighter">
            {L(`Welcome${firstName ? ` ${firstName}` : ""}! 🎉`, `¡Bienvenido${firstName ? ` ${firstName}` : ""}! 🎉`)}
          </h1>
          <p className="mt-4 text-[#4B5563] leading-relaxed max-w-2xl">
            {L(`Your profile is active. Below you'll find your current assignments — tap "I'm here" the moment you arrive at the customer's home.`,
               `Tu perfil está activo. Abajo encontrarás tus asignaciones actuales — toca "Llegué" apenas estés en el hogar del cliente.`)}
          </p>
        </Card>

        {/* Earnings widget (Fase 3, Bloque E) */}
        <Card className="rounded-2xl p-5 border border-[#E5E7EB] flex flex-col sm:flex-row sm:items-center gap-4" data-testid="earnings-widget">
          <span className="w-11 h-11 rounded-xl bg-emerald-100 text-emerald-700 grid place-items-center shrink-0">
            <DollarSign className="w-5 h-5" />
          </span>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-wider text-[#4B5563] font-semibold">
              {L("This month you've earned", "Este mes ganaste")}
            </div>
            <div className="font-mono text-2xl font-bold" data-testid="earnings-widget-amount">
              ${Number(earnings.this_month || 0).toFixed(2)}
            </div>
            {Number(earnings.pending || 0) > 0 && (
              <div className="text-xs text-amber-700 mt-0.5" data-testid="earnings-widget-pending">
                ${Number(earnings.pending).toFixed(2)} {L("pending payout", "pendiente de pago")}
              </div>
            )}
          </div>
          <Button onClick={() => navigate("/cleaner/earnings")} variant="outline" className="rounded-xl shrink-0" data-testid="earnings-widget-link">
            {L("See details", "Ver detalles")}
          </Button>
        </Card>

        {/* Assigned bookings */}
        <Card className="rounded-2xl p-6 border border-[#E5E7EB]" data-testid="cleaner-bookings-card">
          <div className="flex items-center gap-2 mb-3">
            <Briefcase className="w-4 h-4 text-[#0EA5A4]" />
            <h2 className="font-display font-bold text-lg">{L("Your assignments", "Tus asignaciones")}</h2>
          </div>
          {bookings.length === 0 ? (
            <p className="text-sm text-[#9CA3AF]" data-testid="no-assignments">
              {L("No active bookings yet. We'll show them here as soon as the admin assigns one to you.",
                 "No tienes reservas activas todavía. Te las mostramos aquí apenas el admin te asigne una.")}
            </p>
          ) : (
            <div className="space-y-4" data-testid="bookings-list">
              {bookings.map((b) => {
                const addr = [b.address_line1, b.city, b.state, b.zip_code].filter(Boolean).join(", ");
                const alreadyCheckedIn = !!b.checked_in_at;
                const accepted = b.assignment_status === "accepted";
                return (
                  <Card key={b.id} className="rounded-2xl p-5 border border-[#E5E7EB]" data-testid={`booking-${b.id}`}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0">
                        <div className="font-semibold text-sm">#{b.booking_number} · {b.customer_full_name}</div>
                        <div className="text-xs text-[#4B5563] flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5">
                          {b.preferred_date && <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{b.preferred_date}</span>}
                          {b.preferred_time_slot && <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{b.preferred_time_slot}</span>}
                        </div>
                        {addr && <div className="text-xs text-[#9CA3AF] mt-1 flex items-center gap-1"><MapPin className="w-3 h-3" />{addr}</div>}
                      </div>
                      {accepted && (
                        <Badge variant="outline" className="border-emerald-300 text-emerald-700 bg-emerald-50 shrink-0" data-testid={`accepted-badge-${b.id}`}>
                          <CheckCircle2 className="w-3 h-3 mr-1" /> {L("Accepted", "Aceptado")}
                        </Badge>
                      )}
                    </div>
                    {b.earning_amount != null && (
                      <div className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-1.5" data-testid={`earning-${b.id}`}>
                        <DollarSign className="w-3.5 h-3.5" />
                        {L(
                          `You will earn: $${b.earning_amount.toFixed(2)}`,
                          `Vas a ganar: $${b.earning_amount.toFixed(2)}`
                        )}
                      </div>
                    )}
                    <div className="flex flex-wrap gap-2 mt-4">
                      <Button onClick={() => navigate(`/cleaner/bookings/${b.id}`)} variant="outline" className="rounded-xl" data-testid={`view-details-btn-${b.id}`}>
                        <Eye className="w-4 h-4 mr-2" /> {L("View full details", "Ver detalles completos")}
                      </Button>
                      {!accepted && (
                        <>
                          <Button onClick={() => setDeclineTarget(b.id)} disabled={decliningId === b.id}
                            variant="outline" className="rounded-xl text-rose-600 border-rose-300 hover:bg-rose-50 hover:text-rose-700"
                            data-testid={`decline-btn-${b.id}`}>
                            {decliningId === b.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><XCircle className="w-4 h-4 mr-2" /> {L("Decline", "Rechazar")}</>}
                          </Button>
                          <Button onClick={() => acceptJob(b.id)} disabled={acceptingId === b.id}
                            className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`accept-btn-${b.id}`}>
                            {acceptingId === b.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-2" /> {L("Accept job", "Aceptar trabajo")}</>}
                          </Button>
                        </>
                      )}
                      {alreadyCheckedIn ? (
                        <Badge variant="outline" className="border-emerald-300 text-emerald-700 bg-emerald-50 self-center" data-testid={`checked-in-${b.id}`}>
                          <CheckCircle2 className="w-3 h-3 mr-1" /> {L("Checked in", "Llegada confirmada")}
                        </Badge>
                      ) : (
                        <Button
                          onClick={() => setCheckinFor(b.id)}
                          disabled={!accepted}
                          title={!accepted ? L("Accept the job first", "Acepta el trabajo primero") : ""}
                          className="rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white disabled:opacity-40"
                          data-testid={`checkin-btn-${b.id}`}
                        >
                          <MapPin className="w-4 h-4 mr-2" />
                          {L("I'm here", "Llegué")}
                        </Button>
                      )}
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </Card>

        {/* Profile */}
        {profile && (
          <Card className="rounded-2xl p-6 border border-[#E5E7EB]" data-testid="cleaner-profile-card">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-4">
                <Avatar src={profile.photo_url} name={profile.full_name} />
                <div>
                  <div className="font-display text-xl font-bold">{profile.full_name}</div>
                  <div className="text-xs text-[#9CA3AF] mt-0.5">{profile.email} · {profile.phone || "—"}</div>
                </div>
              </div>
              <Button onClick={() => setEditing(true)} variant="outline" className="rounded-xl shrink-0" data-testid="edit-profile-btn">
                <Pencil className="w-4 h-4 mr-2" /> {L("Edit my profile", "Editar mi perfil")}
              </Button>
            </div>
          </Card>
        )}

        {/* Account / Security (iter21 — Fix 3) */}
        <Card className="rounded-2xl p-6 border border-[#E5E7EB]" data-testid="cleaner-security-card">
          <div className="flex items-center gap-2 mb-3">
            <KeyRound className="w-4 h-4 text-[#0EA5A4]" />
            <h2 className="font-display font-bold text-lg">{L("Account & security", "Cuenta y seguridad")}</h2>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="text-sm">
              <div className="text-[#9CA3AF] text-xs">{L("Email", "Correo")}</div>
              <div className="font-semibold" data-testid="security-email">{me?.user?.email || profile?.email || "—"}</div>
            </div>
            <Button onClick={() => navigate("/cleaner/change-password")} variant="outline" className="rounded-xl shrink-0" data-testid="change-password-btn">
              <KeyRound className="w-4 h-4 mr-2" /> {L("Change password", "Cambiar contraseña")}
            </Button>
          </div>
        </Card>
      </main>

      {editing && profile && (
        <EditProfileModal profile={profile} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); load(); }} L={L} locale={locale} />
      )}

      <PayoutSetupDialog
        open={payoutExplainerOpen}
        onOpenChange={(o) => { if (!payoutLoading) setPayoutExplainerOpen(o); }}
        onContinue={startPayoutSetup}
        loading={payoutLoading}
        locale={locale}
      />

      <LiveSelfieCapture
        open={recheckOpen}
        onOpenChange={(o) => { if (!o) setRecheckOpen(false); }}
        title={L("Re-verify your identity", "Re-verifica tu identidad")}
        description={L("Center your face inside the frame and tap Capture. We'll review it within 24 hours.",
                       "Centra tu cara en el marco y toca Capturar. Lo revisamos dentro de las 24 horas.")}
        onCapture={submitRecheckSelfie}
        submitting={recheckSubmitting}
      />

      <CheckInModal
        open={!!checkinFor}
        onOpenChange={(o) => { if (!o) setCheckinFor(null); }}
        onSubmit={submitCheckin}
        submitting={checkinSubmitting}
      />

      <AlertDialog open={!!declineTarget} onOpenChange={(o) => { if (!o) setDeclineTarget(null); }}>
        <AlertDialogContent data-testid="decline-job-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{L("Decline this job?", "¿Rechazar este trabajo?")}</AlertDialogTitle>
            <AlertDialogDescription>
              {L("Are you sure you want to decline this job? You won't be able to reverse this.",
                 "¿Estás seguro de que quieres rechazar este trabajo? No vas a poder revertirlo.")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="decline-job-cancel-btn">{L("Cancel", "Cancelar")}</AlertDialogCancel>
            <AlertDialogAction onClick={declineJob} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="decline-job-confirm-btn">
              {L("Decline job", "Rechazar trabajo")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function EditProfileModal({ profile, onClose, onSaved, L, locale }) {
  const [tasks, setTasks] = useState([]);
  const [form, setForm] = useState({
    phone: profile.phone || "", city: profile.city || "", bio: profile.bio || "",
    languages: profile.languages || [], specialties: profile.specialties || [],
  });
  const [saving, setSaving] = useState(false);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const toggle = (key, val) => set({ [key]: form[key].includes(val) ? form[key].filter((x) => x !== val) : [...form[key], val] });

  useEffect(() => { api.get("/public/specialties").then((r) => setTasks((unwrap(r) || []).map((s) => ({ code: s.slug, label_en: s.label_en, label_es: s.label_es })))).catch(() => {}); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await cleanerApi.put("/cleaners/profile", form);
      toast.success(L("Profile updated", "Perfil actualizado"));
      onSaved();
    } catch { toast.error(L("Could not save", "No pudimos guardar")); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-profile-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tighter">{L("Edit my profile", "Editar mi perfil")}</DialogTitle>
          <DialogDescription>{L("Keep your contact info and skills up to date.", "Mantén actualizada tu info de contacto y habilidades.")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label className="text-xs">{L("Phone", "Teléfono")}</Label><Input value={form.phone} onChange={(e) => set({ phone: e.target.value })} className="rounded-xl mt-1" data-testid="profile-phone" /></div>
            <div><Label className="text-xs">{L("City", "Ciudad")}</Label><Input value={form.city} onChange={(e) => set({ city: e.target.value })} className="rounded-xl mt-1" data-testid="profile-city" /></div>
          </div>
          <div>
            <Label className="text-xs">{L("Languages", "Idiomas")}</Label>
            <div className="flex flex-wrap gap-2 mt-1.5">
              {LANGUAGES.map((l) => <Chip key={l.code} active={form.languages.includes(l.code)} onClick={() => toggle("languages", l.code)}>{l.label}</Chip>)}
            </div>
          </div>
          <div>
            <Label className="text-xs">{L("Specialties", "Especialidades")}</Label>
            <div className="flex flex-wrap gap-2 mt-1.5">
              {tasks.map((tk) => <Chip key={tk.code} active={form.specialties.includes(tk.code)} onClick={() => toggle("specialties", tk.code)}>{locale === "es" ? (tk.label_es || tk.code) : (tk.label_en || tk.code)}</Chip>)}
            </div>
          </div>
          <div><Label className="text-xs">{L("Bio", "Biografía")}</Label><Textarea rows={3} value={form.bio} onChange={(e) => set({ bio: e.target.value })} className="rounded-xl mt-1" data-testid="profile-bio" /></div>
        </div>
        <div className="flex gap-2 justify-end pt-2">
          <Button variant="outline" className="rounded-xl" onClick={onClose}>{L("Cancel", "Cancelar")}</Button>
          <Button onClick={save} disabled={saving} className="rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white" data-testid="profile-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-2" /> {L("Save", "Guardar")}</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Chip({ active, onClick, children }) {
  return (
    <button type="button" onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${active ? "bg-[#0EA5A4] text-white border-[#0EA5A4]" : "bg-white text-[#4B5563] border-[#E5E7EB] hover:border-[#0EA5A4]"}`}>
      {children}
    </button>
  );
}
